import React, { useState, useEffect, useCallback } from "react";
import ReactDOM from "react-dom";
import api from "@/utils/api";
import { useAuth } from "@/context/AuthContext";
import {
  Database, Plus, Search, RefreshCw, MapPin, User, CheckCircle,
  Clock, ChevronRight, Calendar, Play, Eye, Users, AlertTriangle, Zap
} from "lucide-react";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";

const PRIORITY_COLORS = {
  high: { bg: "bg-red-100", text: "text-red-700", label: "High" },
  normal: { bg: "bg-blue-100", text: "text-blue-700", label: "Normal" },
  low: { bg: "bg-slate-100", text: "text-slate-700", label: "Low" },
};

const STATUS_COLORS = {
  assigned: { bg: "bg-yellow-100", text: "text-yellow-700", label: "Assigned" },
  in_progress: { bg: "bg-blue-100", text: "text-blue-700", label: "In Progress" },
  completed: { bg: "bg-green-100", text: "text-green-700", label: "Completed" },
  cancelled: { bg: "bg-slate-100", text: "text-slate-700", label: "Cancelled" },
};

export default function FeederAssignmentsPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const isSurveyor = user?.role === "surveyor" || user?.role === "technician";
  const canAssign = ["admin", "package_head", "ci", "di"].includes(user?.role);

  const [assignments, setAssignments] = useState([]);
  const [surveyors, setSurveyors] = useState([]);
  const [feeders, setFeeders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);

  // For surveyor's offline-ready view
  const [myData, setMyData] = useState(null);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      if (isSurveyor) {
        // Surveyor: Get their assignments with DT data for offline cache
        const res = await api.get("/feeder-assignments/my-assignments");
        setMyData(res.data);
        setAssignments(res.data.assignments || []);
      } else {
        // Admin/CI: Get all assignments
        const [assignRes, surveyorRes, feederRes] = await Promise.all([
          api.get("/feeder-assignments"),
          api.get("/feeder-assignments/surveyors"),
          api.get("/hierarchy/feeders")
        ]);
        setAssignments(assignRes.data.items || []);
        setSurveyors(surveyorRes.data || []);
        setFeeders(feederRes.data || []);
      }
    } catch (e) {
      toast.error("Failed to load data");
    } finally {
      setLoading(false);
    }
  }, [isSurveyor]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleStart = async (id) => {
    try {
      await api.put(`/feeder-assignments/${id}/start`);
      toast.success("Assignment started");
      fetchData();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    }
  };

  const handleComplete = async (id) => {
    try {
      const res = await api.put(`/feeder-assignments/${id}/complete`);
      toast.success(`Completed! ${res.data.surveyed_count}/${res.data.dt_count} DTs surveyed`);
      fetchData();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    }
  };

  return (
    <div className="p-4 md:p-6 animate-fade-up" data-testid="feeder-assignments-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6">
        <div>
          <h1 className="text-2xl font-black text-slate-900" style={{ fontFamily: "Manrope" }}>
            {isSurveyor ? "My Survey Feeders" : "Feeder Assignments"}
          </h1>
          <p className="text-slate-500 text-sm">
            {isSurveyor 
              ? `${assignments.length} feeders assigned · ${myData?.dts?.length || 0} DTs to survey`
              : `${assignments.length} assignments · Assign feeders to surveyors`
            }
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={fetchData} className="p-2 border border-slate-200 rounded-sm hover:bg-slate-50" data-testid="refresh-btn">
            <RefreshCw size={16} className={loading ? "animate-spin text-orange-500" : "text-slate-500"} />
          </button>
          {canAssign && (
            <button
              onClick={() => setShowCreate(true)}
              className="flex items-center gap-2 px-4 py-2 bg-orange-500 text-white rounded-sm font-bold text-sm hover:bg-orange-600"
              data-testid="assign-feeder-btn"
            >
              <Plus size={16} />Assign Feeder
            </button>
          )}
        </div>
      </div>

      {/* Surveyor View */}
      {isSurveyor ? (
        <SurveyorView 
          assignments={assignments} 
          myData={myData} 
          onStart={handleStart}
          onComplete={handleComplete}
          navigate={navigate}
          loading={loading}
        />
      ) : (
        <AdminView 
          assignments={assignments}
          loading={loading}
          navigate={navigate}
        />
      )}

      {/* Create Assignment Modal */}
      {showCreate && (
        <CreateAssignmentModal
          feeders={feeders}
          surveyors={surveyors}
          onClose={() => setShowCreate(false)}
          onCreated={() => { setShowCreate(false); fetchData(); }}
        />
      )}
    </div>
  );
}

// =============================================================================
// SURVEYOR VIEW
// =============================================================================
function SurveyorView({ assignments, myData, onStart, onComplete, navigate, loading }) {
  if (loading) return <LoadingState />;

  if (!assignments || assignments.length === 0) {
    return (
      <div className="bg-white border border-slate-200 rounded-sm p-8 text-center">
        <Database size={48} className="mx-auto mb-4 text-slate-300" />
        <h3 className="text-lg font-bold text-slate-700 mb-2">No Feeders Assigned</h3>
        <p className="text-slate-500 text-sm">Contact your Circle Incharge to get feeders assigned for survey.</p>
      </div>
    );
  }

  // Group DTs by feeder_code
  const dtsByFeeder = {};
  (myData?.dts || []).forEach(dt => {
    const key = dt.feeder_code;
    if (!dtsByFeeder[key]) dtsByFeeder[key] = [];
    dtsByFeeder[key].push(dt);
  });

  return (
    <div className="space-y-4">
      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <StatCard label="Feeders" value={assignments.length} color="text-blue-600" bgColor="bg-blue-50" />
        <StatCard label="Total DTs" value={myData?.dts?.length || 0} color="text-slate-600" />
        <StatCard label="Surveyed" value={(myData?.dts || []).filter(d => d.has_survey).length} color="text-green-600" bgColor="bg-green-50" />
        <StatCard label="Pending" value={(myData?.dts || []).filter(d => !d.has_survey).length} color="text-orange-600" bgColor="bg-orange-50" />
      </div>

      {/* Last Sync */}
      {myData?.last_sync && (
        <div className="bg-blue-50 border border-blue-200 rounded-sm p-2 text-xs text-blue-700">
          <RefreshCw size={12} className="inline mr-1" />
          Data synced: {new Date(myData.last_sync).toLocaleString()}
        </div>
      )}

      {/* Feeder Cards */}
      <div className="space-y-3">
        {assignments.map(a => {
          const dts = dtsByFeeder[a.feeder_code] || [];
          const surveyedCount = dts.filter(d => d.has_survey).length;
          const totalDTs = dts.length;
          const status = STATUS_COLORS[a.status] || STATUS_COLORS.assigned;
          const priority = PRIORITY_COLORS[a.priority] || PRIORITY_COLORS.normal;

          return (
            <div key={a.id} className="bg-white border border-slate-200 rounded-sm overflow-hidden">
              <div className="p-4">
                <div className="flex items-start justify-between mb-3">
                  <div className="flex-1">
                    {/* Hierarchy Info - Division > SubDivision > Substation */}
                    <div className="text-[10px] text-slate-400 mb-1 font-medium">
                      {[a.division_name, a.sub_division_name, a.sub_station_name].filter(Boolean).join(" → ") || a.circle_name}
                    </div>
                    <div className="flex items-center gap-2 flex-wrap">
                      <h3 className="font-bold text-slate-800">{a.feeder_name || a.feeder_code}</h3>
                      <span className="text-xs text-slate-400 font-mono">({a.feeder_code})</span>
                      <span className={`px-1.5 py-0.5 rounded text-[10px] font-bold ${status.bg} ${status.text}`}>
                        {status.label}
                      </span>
                      <span className={`px-1.5 py-0.5 rounded text-[10px] font-bold ${priority.bg} ${priority.text}`}>
                        {priority.label}
                      </span>
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="text-2xl font-black text-orange-600">{totalDTs}</div>
                    <div className="text-[10px] text-slate-500 uppercase">DTs</div>
                  </div>
                </div>

                {/* Progress Bar */}
                {totalDTs > 0 && (
                  <div className="mb-3">
                    <div className="flex justify-between text-xs mb-1">
                      <span className="text-green-600 font-medium">{surveyedCount} Surveyed</span>
                      <span className="text-orange-600 font-medium">{totalDTs - surveyedCount} Pending</span>
                    </div>
                    <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
                      <div className="h-full bg-green-500 transition-all" style={{ width: `${Math.round((surveyedCount / totalDTs) * 100)}%` }}></div>
                    </div>
                  </div>
                )}

                {/* Actions */}
                <div className="flex gap-2">
                  {a.status === "assigned" && (
                    <button
                      onClick={() => onStart(a.id)}
                      className="flex-1 flex items-center justify-center gap-1 px-3 py-2 bg-blue-500 text-white rounded-sm text-xs font-bold hover:bg-blue-600"
                    >
                      <Play size={14} />Start Work
                    </button>
                  )}
                  {(a.status === "in_progress" || a.status === "assigned") && (
                    <button
                      onClick={() => navigate(`/survey/new?feeder=${a.feeder_code}`)}
                      className="flex-1 flex items-center justify-center gap-1 px-3 py-2 bg-green-500 text-white rounded-sm text-xs font-bold hover:bg-green-600"
                    >
                      <Plus size={14} />New Survey
                    </button>
                  )}
                  {totalDTs > 0 && (
                    <button
                      onClick={() => navigate(`/dt-master?feeder_code=${a.feeder_code}`)}
                      className="flex items-center justify-center gap-1 px-3 py-2 border border-slate-200 text-slate-600 rounded-sm text-xs font-bold hover:bg-slate-50"
                    >
                      <Eye size={14} />View
                    </button>
                  )}
                </div>
              </div>

              {/* Existing DTs List */}
              {dts.length > 0 && (
                <div className="border-t border-slate-100 bg-slate-50 p-3">
                  <div className="text-[10px] font-bold text-slate-500 uppercase mb-2">Existing DTs ({dts.length})</div>
                  <div className="flex flex-wrap gap-1">
                    {dts.slice(0, 8).map(dt => (
                      <button
                        key={dt.id}
                        onClick={() => navigate(`/dt-master/${dt.id}/survey`)}
                        className={`px-2 py-1 border rounded text-[10px] font-mono ${
                          dt.has_survey 
                            ? "bg-green-50 border-green-200 text-green-700" 
                            : "bg-white border-orange-200 text-orange-700 hover:bg-orange-50"
                        }`}
                      >
                        {dt.dt_code} {dt.has_survey && "✓"}
                      </button>
                    ))}
                    {dts.length > 8 && (
                      <span className="px-2 py-1 text-[10px] text-slate-400">+{dts.length - 8} more</span>
                    )}
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

// =============================================================================
// ADMIN VIEW
// =============================================================================
function AdminView({ assignments, loading, navigate }) {
  if (loading) return <LoadingState />;

  return (
    <div className="bg-white border border-slate-200 rounded-sm overflow-hidden">
      <table className="dt-table">
        <thead>
          <tr>
            <th>Feeder</th>
            <th>Surveyor</th>
            <th>Priority</th>
            <th>DTs</th>
            <th>Progress</th>
            <th>Status</th>
            <th>Assigned</th>
          </tr>
        </thead>
        <tbody>
          {assignments.length === 0 ? (
            <tr><td colSpan={7} className="text-center py-8 text-slate-400">No assignments found</td></tr>
          ) : assignments.map(a => {
            const status = STATUS_COLORS[a.status] || STATUS_COLORS.assigned;
            const priority = PRIORITY_COLORS[a.priority] || PRIORITY_COLORS.normal;
            return (
              <tr key={a.id} className="hover:bg-slate-50 cursor-pointer" onClick={() => navigate(`/dt-master?feeder_id=${a.feeder_id}`)}>
                <td>
                  <div className="font-mono font-bold text-slate-800">{a.feeder_code}</div>
                  <div className="text-[10px] text-slate-400">{a.feeder_name}</div>
                </td>
                <td className="text-sm">{a.surveyor_name || "—"}</td>
                <td><span className={`px-1.5 py-0.5 rounded text-[10px] font-bold ${priority.bg} ${priority.text}`}>{priority.label}</span></td>
                <td className="text-sm font-medium">{a.dt_count}</td>
                <td>
                  <div className="flex items-center gap-2">
                    <div className="w-16 h-1.5 bg-slate-100 rounded-full overflow-hidden">
                      <div className="h-full bg-green-500" style={{ width: `${a.progress_percent || 0}%` }}></div>
                    </div>
                    <span className="text-xs text-slate-500">{a.progress_percent || 0}%</span>
                  </div>
                </td>
                <td><span className={`px-1.5 py-0.5 rounded text-[10px] font-bold ${status.bg} ${status.text}`}>{status.label}</span></td>
                <td className="text-xs text-slate-500">{a.assigned_at ? new Date(a.assigned_at).toLocaleDateString() : "—"}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

// =============================================================================
// CREATE ASSIGNMENT MODAL
// =============================================================================
function CreateAssignmentModal({ feeders, surveyors, onClose, onCreated }) {
  const [loading, setLoading] = useState(false);
  const [availableFeeders, setAvailableFeeders] = useState([]);
  const [form, setForm] = useState({
    feeder_code: "",
    surveyor_id: "",
    priority: "normal",
    target_date: "",
    remarks: "",
    allowed_tasks: ["survey", "mi", "dtm", "dtc"]  // Default: all tasks
  });

  // Available tasks
  const AVAILABLE_TASKS = [
    { key: "survey", label: "Survey", description: "Site survey" },
    { key: "mi", label: "MI", description: "Meter Installation" },
    { key: "dtm", label: "DTM", description: "DT Metering" },
    { key: "dtc", label: "DTC", description: "DT Commissioning" }
  ];

  // Toggle task selection
  const toggleTask = (task) => {
    setForm(prev => ({
      ...prev,
      allowed_tasks: prev.allowed_tasks.includes(task)
        ? prev.allowed_tasks.filter(t => t !== task)
        : [...prev.allowed_tasks, task]
    }));
  };

  // Fetch feeders from hierarchy
  useEffect(() => {
    const fetchFeeders = async () => {
      try {
        const res = await api.get("/hierarchy/feeders");
        setAvailableFeeders(res.data || []);
      } catch {}
    };
    fetchFeeders();
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.feeder_code || !form.surveyor_id) {
      toast.error("Feeder and Surveyor are required");
      return;
    }
    if (form.allowed_tasks.length === 0) {
      toast.error("Please select at least one task");
      return;
    }
    setLoading(true);
    try {
      // Clean payload - remove empty strings, convert to null
      const payload = {
        feeder_code: form.feeder_code,
        surveyor_id: form.surveyor_id,
        priority: form.priority || "normal",
        target_date: form.target_date || null,
        remarks: form.remarks || null,
        allowed_tasks: form.allowed_tasks
      };
      await api.post("/feeder-assignments", payload);
      toast.success("Feeder assigned successfully");
      if (onCreated) {
        onCreated();
      }
    } catch (err) {
      console.error("Assignment error:", err);
      toast.error(err.response?.data?.detail || "Failed to assign feeder");
    } finally {
      setLoading(false);
    }
  };

  return ReactDOM.createPortal(
    <div className="fixed inset-0 z-[100] bg-black/50 flex items-center justify-center p-4">
      <div className="bg-white rounded-sm shadow-xl w-full max-w-md max-h-[90vh] flex flex-col">
        <div className="px-6 py-4 border-b border-slate-200 flex-shrink-0">
          <h3 className="text-lg font-bold">Assign Feeder to Surveyor</h3>
        </div>
        <form onSubmit={handleSubmit} className="p-6 space-y-4 overflow-y-auto flex-1">
          <div>
            <label className="field-label">Feeder *</label>
            <select 
              className="field-select" 
              value={form.feeder_code} 
              onChange={e => setForm({ ...form, feeder_code: e.target.value })}
              required
            >
              <option value="">Select feeder</option>
              {availableFeeders.map(f => (
                <option key={f.feeder_code} value={f.feeder_code}>
                  {f.feeder_name || f.feeder_code} ({f.feeder_code}) - {f.circle_name}
                </option>
              ))}
            </select>
            {form.feeder_code && (
              <p className="text-[10px] text-slate-400 mt-1">
                {availableFeeders.find(f => f.feeder_code === form.feeder_code)?.division_name && (
                  <>
                    {availableFeeders.find(f => f.feeder_code === form.feeder_code)?.division_name}
                    {availableFeeders.find(f => f.feeder_code === form.feeder_code)?.sub_division_name && ` → ${availableFeeders.find(f => f.feeder_code === form.feeder_code)?.sub_division_name}`}
                    {availableFeeders.find(f => f.feeder_code === form.feeder_code)?.sub_station_name && ` → ${availableFeeders.find(f => f.feeder_code === form.feeder_code)?.sub_station_name}`}
                  </>
                )}
              </p>
            )}
          </div>
          <div>
            <label className="field-label">Surveyor *</label>
            <select 
              className="field-select" 
              value={form.surveyor_id} 
              onChange={e => setForm({ ...form, surveyor_id: e.target.value })}
              required
            >
              <option value="">Select surveyor</option>
              {surveyors.map(s => (
                <option key={s.id} value={s.id}>
                  {s.full_name} ({s.role}) - {s.circle_name}
                </option>
              ))}
            </select>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="field-label">Priority</label>
              <select 
                className="field-select" 
                value={form.priority} 
                onChange={e => setForm({ ...form, priority: e.target.value })}
              >
                <option value="high">High</option>
                <option value="normal">Normal</option>
                <option value="low">Low</option>
              </select>
            </div>
            <div>
              <label className="field-label">Target Date</label>
              <input 
                type="date" 
                className="field-input" 
                value={form.target_date} 
                onChange={e => setForm({ ...form, target_date: e.target.value })}
              />
            </div>
          </div>
          <div>
            <label className="field-label">Remarks</label>
            <textarea 
              className="field-input h-16 resize-none" 
              placeholder="Any special instructions..."
              value={form.remarks}
              onChange={e => setForm({ ...form, remarks: e.target.value })}
            />
          </div>

          {/* Allowed Tasks Section */}
          <div className="border-t border-slate-200 pt-4">
            <label className="field-label mb-2 flex items-center gap-2">
              <Zap size={14} className="text-orange-500" />
              Allowed Tasks for this Assignment
            </label>
            <p className="text-[10px] text-slate-500 mb-3">
              Select which tasks the assignee can perform on this feeder
            </p>
            <div className="grid grid-cols-2 gap-2">
              {AVAILABLE_TASKS.map(task => (
                <label 
                  key={task.key} 
                  className={`flex items-center gap-2 p-2 border rounded-sm cursor-pointer transition-colors ${
                    form.allowed_tasks.includes(task.key) 
                      ? "border-orange-300 bg-orange-50" 
                      : "border-slate-200 hover:bg-slate-50"
                  }`}
                >
                  <input
                    type="checkbox"
                    checked={form.allowed_tasks.includes(task.key)}
                    onChange={() => toggleTask(task.key)}
                    className="rounded border-slate-300 text-orange-500 focus:ring-orange-500"
                  />
                  <div>
                    <div className="text-xs font-semibold text-slate-700">{task.label}</div>
                    <div className="text-[10px] text-slate-400">{task.description}</div>
                  </div>
                </label>
              ))}
            </div>
            {form.allowed_tasks.length === 0 && (
              <p className="text-[10px] text-red-500 mt-2">⚠ Select at least one task</p>
            )}
          </div>

          <div className="flex gap-3 pt-4">
            <button type="button" onClick={onClose} className="flex-1 px-4 py-2 border border-slate-200 rounded-sm text-sm font-semibold">
              Cancel
            </button>
            <button type="submit" disabled={loading} className="flex-1 px-4 py-2 bg-orange-500 text-white rounded-sm text-sm font-bold disabled:opacity-60">
              {loading ? "Assigning..." : "Assign Feeder"}
            </button>
          </div>
        </form>
      </div>
    </div>,
    document.body
  );
}

// =============================================================================
// HELPERS
// =============================================================================
function StatCard({ label, value, color = "text-slate-800", bgColor = "bg-white" }) {
  return (
    <div className={`${bgColor} border border-slate-200 rounded-sm p-3`}>
      <div className="text-[10px] text-slate-500 uppercase font-bold tracking-wide">{label}</div>
      <div className={`text-2xl font-black ${color}`}>{value}</div>
    </div>
  );
}

function LoadingState() {
  return (
    <div className="bg-white border border-slate-200 rounded-sm p-8 text-center">
      <RefreshCw size={24} className="mx-auto animate-spin text-orange-500 mb-2" />
      <p className="text-slate-500 text-sm">Loading...</p>
    </div>
  );
}
